import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { orderService } from "../services/apiService";

const PendingPayments = () => {
  const [mobile, setMobile] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("P");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState("");
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [collectPopup, setCollectPopup] = useState(null);
  const [collectForm, setCollectForm] = useState({ amount: "", paymentMode: "", transactionId: "" });
  const [collectLoading, setCollectLoading] = useState(false);

  const fetchPending = async (pageNo = 0, override = null) => {
    setLoading(true);
    try {
      const mob = (override?.mobile ?? mobile).trim() || null;
      const status = override?.paymentStatus ?? paymentStatus;
      const data = await orderService.searchPendingPayments({
        customerMobile: mob,
        paymentStatus: status,
        page: pageNo,
        size: 15,
      });
      setResults(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(pageNo);
    } catch {
      setResults([]);
      setToast({ type: "error", message: "Failed to fetch payments" });
    } finally {
      setLoading(false);
    }
  };

  // On mount, honor an AI "open this page" handoff (prefill + run); otherwise
  // do the normal unfiltered load. Reuses the existing fetchPending.
  useEffect(() => {
    let nav = null;
    try {
      const raw = sessionStorage.getItem("jashNav");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.page === "pending_payments") nav = parsed;
      }
    } catch { nav = null; }
    if (nav) {
      try { sessionStorage.removeItem("jashNav"); } catch { /* noop */ }
      const p = nav.prefill || {};
      if (p.customerMobile) setMobile(p.customerMobile);
      if (p.paymentStatus) setPaymentStatus(p.paymentStatus);
      if (nav.autoSearch !== false) {
        fetchPending(0, {
          mobile: p.customerMobile || "",
          paymentStatus: p.paymentStatus || "P",
        });
        return;
      }
    }
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPending(0);
  };

  const handleReset = () => {
    setMobile("");
    setPaymentStatus("P");
    fetchPending(0);
  };

  const handleDownload = async (type) => {
    setReportLoading(type);
    try {
      await orderService.downloadPaymentReport(type, {
        customerMobile: mobile.trim() || null,
        paymentStatus,
      });
    } catch {
      setToast({ type: "error", message: `Failed to download ${type.toUpperCase()} report` });
    } finally {
      setReportLoading("");
    }
  };

const openCollect = (order) => {
    setCollectPopup(order);
    setCollectForm({ amount: order.dueAmount?.toString() || "", paymentMode: "", transactionId: "" });
  };

  const handleCollect = async () => {
    const amount = parseFloat(collectForm.amount);
    if (!amount || amount <= 0) {
      setToast({ type: "error", message: "Enter a valid amount" });
      return;
    }
    if (amount > collectPopup.dueAmount) {
      setToast({ type: "error", message: `Amount cannot exceed due amount (₹${collectPopup.dueAmount})` });
      return;
    }
    if (!collectForm.paymentMode) {
      setToast({ type: "error", message: "Select payment mode" });
      return;
    }
    if (!collectForm.transactionId.trim()) {
      setToast({ type: "error", message: "Enter transaction ID" });
      return;
    }

    const confirmed = window.confirm(
      `Confirm collecting ₹${amount.toFixed(2)} from ${collectPopup.customerName || collectPopup.customerMobile} for Order #${collectPopup.orderId}?`
    );
    if (!confirmed) return;

    setCollectLoading(true);
    try {
      await orderService.collectPayment({
        orderId: collectPopup.orderId,
        amount,
        paymentMode: collectForm.paymentMode,
        transactionId: collectForm.transactionId.trim(),
      });
      setToast({ type: "success", message: `₹${amount.toFixed(2)} collected for Order #${collectPopup.orderId}` });
      setCollectPopup(null);
      fetchPending(page);
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.message || err.response?.data?.errorMessage || "Failed to collect payment" });
    } finally {
      setCollectLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Search */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          💰 Payment Search
        </h2>
        <div className="p-2">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment Type</label>
              <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white">
                <option value="P">Pending</option>
                <option value="F">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Customer Mobile</label>
              <input type="tel" value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile" maxLength="10"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <div className="flex items-end gap-2">
              <button type="button" onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
                <i className="fas fa-redo" /> Reset
              </button>
              <button type="submit" disabled={loading}
                className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
                }`}>
                {loading ? <><i className="fas fa-spinner fa-spin" /> Searching...</> : <><i className="fas fa-search" /> Search</>}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 {paymentStatus === "P" ? "Pending" : "Completed"} Payments
            {totalElements > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({totalElements} records)</span>}
          </h2>
          {results.length > 0 && (
            <div className="flex gap-2">
              <button onClick={() => handleDownload("pdf")} disabled={reportLoading === "pdf"}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow transition disabled:opacity-50">
                {reportLoading === "pdf" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-pdf" />} PDF
              </button>
              <button onClick={() => handleDownload("excel")} disabled={reportLoading === "excel"}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold shadow transition disabled:opacity-50">
                {reportLoading === "excel" ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-excel" />} Excel
              </button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto flex-1">
            <table className="min-w-full text-sm text-gray-800 table-auto">
              <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">#</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Bill No</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Customer</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Mobile</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider">Bill Amount</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider">Paid</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider">Due</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12">
                      <i className="fas fa-circle-notch fa-spin text-blue-500 text-2xl"></i>
                      <p className="text-sm text-gray-400 mt-2">Loading...</p>
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12">
                      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="fas fa-check-circle text-green-400 text-2xl"></i>
                      </div>
                      <p className="text-gray-500 font-medium">No pending payments</p>
                      <p className="text-xs text-gray-400 mt-1">All customers have cleared their dues</p>
                    </td>
                  </tr>
                ) : (
                  results.map((item, idx) => (
                    <tr key={item.orderId} className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50 transition-colors`}>
                      <td className="py-3 px-4 text-sm text-gray-500">{page * 15 + idx + 1}</td>
                      <td className="py-3 px-4 text-sm font-medium text-indigo-700">{item.billNo || `#${item.orderId}`}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{item.customerName || "—"}</td>
                      <td className="py-3 px-4">
                        <span className="text-sm bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                          {item.customerMobile || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-gray-700">
                        ₹{item.finalAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-green-600 font-medium">
                        ₹{item.paidAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          ₹{item.dueAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-xs text-gray-500">
                        {item.createdOn ? new Date(item.createdOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {paymentStatus === "P" ? (
                          <button
                            onClick={() => openCollect(item)}
                            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-all"
                          >
                            <i className="fas fa-rupee-sign text-[10px]"></i> Collect
                          </button>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Paid</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages} ({totalElements} records)</span>
              <div className="flex items-center gap-1">
                <button onClick={() => fetchPending(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
                <button onClick={() => fetchPending(page - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
                <button onClick={() => fetchPending(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
                <button onClick={() => fetchPending(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
              </div>
            </div>
          )}
        </section>

      {/* Collect Payment Popup */}
      {collectPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Popup Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className="fas fa-hand-holding-usd text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Collect Payment</h3>
                    <p className="text-green-100 text-xs">Order #{collectPopup.orderId}</p>
                  </div>
                </div>
                <button onClick={() => setCollectPopup(null)} className="text-white/70 hover:text-white">
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>

            {/* Order Info */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium text-gray-800">{collectPopup.customerName || collectPopup.customerMobile}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bill No</p>
                  <p className="font-medium text-gray-800">{collectPopup.billNo || `#${collectPopup.orderId}`}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bill Amount</p>
                  <p className="font-semibold text-gray-800">₹{collectPopup.finalAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Due Amount</p>
                  <p className="font-bold text-red-600">₹{collectPopup.dueAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Amount to Collect *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <input
                    type="number"
                    value={collectForm.amount}
                    onChange={(e) => setCollectForm({ ...collectForm, amount: e.target.value })}
                    max={collectPopup.dueAmount}
                    step="0.01"
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm font-medium"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Payment Mode *</label>
                <select
                  value={collectForm.paymentMode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setCollectForm({ ...collectForm, paymentMode: mode, transactionId: mode === "CASH" ? "NA" : collectForm.transactionId === "NA" ? "" : collectForm.transactionId });
                  }}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                >
                  <option value="">Select mode</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Transaction ID *</label>
                <input
                  type="text"
                  value={collectForm.transactionId}
                  onChange={(e) => setCollectForm({ ...collectForm, transactionId: e.target.value })}
                  readOnly={collectForm.paymentMode === "CASH"}
                  placeholder={collectForm.paymentMode === "CASH" ? "NA" : "Enter transaction ID"}
                  className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${collectForm.paymentMode === "CASH" ? "bg-gray-100 cursor-not-allowed" : ""}`}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setCollectPopup(null)}
                className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 text-sm font-medium transition">
                Cancel
              </button>
              <button
                onClick={handleCollect}
                disabled={collectLoading}
                className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-2 rounded-lg shadow-md transition-all disabled:opacity-50"
              >
                {collectLoading ? <i className="fas fa-circle-notch fa-spin"></i> : <i className="fas fa-check"></i>}
                {collectLoading ? "Processing..." : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingPayments;
